import { Stack, StackProps } from '@mui/material';
import React, {
  FC,
  FocusEvent,
  PropsWithChildren,
  ReactNode,
  useEffect,
} from 'react';
import { Link } from 'react-router-dom';
import { Texts } from '../../../shared/types';
import { BhabiWordmark } from '../../../shared/components/bhabi-wordmark';
import { useLandscapeMode } from '@/shared/hooks/use-Landscape';
import { useNativeKeyboardBehavior } from '@/shared/hooks/use-ios-keyboard';
import '@/theme/ios-screen.css';

/**
 * When the keyboard takes the bottom of the screen the card becomes the only
 * (bounded) scroll region; keep the focused field in view inside it.
 */
const keepFieldVisible = (e: FocusEvent<HTMLElement>) => {
  const target = e.target as HTMLElement;
  if (!target.matches?.('input, textarea, select')) return;
  window.setTimeout(() => {
    target.scrollIntoView?.({ block: 'nearest' });
  }, 320);
};

/** @deprecated legacy export kept for compatibility; use `AuthFrame`. */
export const MainFrameWrapper: FC<PropsWithChildren> = ({ children }) => (
  <>{children}</>
);

export interface AuthFrameProps {
  /** Page heading inside the card. */
  title?: ReactNode;
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  /** Document title (defaults to a string `title`). */
  documentTitle?: string;
  /** Content under the card body (links, alt sign-in…). */
  footer?: ReactNode;
}

/**
 * The shared auth frame: brand + dealt cards beside (wide / landscape) or
 * above (portrait) an ivory-paper card. Used by sign-in, sign-up, password
 * reset and email verification.
 */
export const AuthFrame: FC<PropsWithChildren<AuthFrameProps>> = ({
  title,
  eyebrow,
  subtitle,
  documentTitle,
  footer,
  children,
}) => {
  const isLandscapeMode = useLandscapeMode();
  // Native: drive --kb-inset so the bounded frame shrinks above the keyboard.
  useNativeKeyboardBehavior();
  const docTitle =
    documentTitle ?? (typeof title === 'string' ? title : undefined);

  useEffect(() => {
    if (docTitle) document.title = `${docTitle} · Bhabi`;
  }, [docTitle]);

  return (
    <div
      className={`bh-auth bh-auth--screen ${
        isLandscapeMode ? 'bh-auth--split' : ''
      }`}
    >
      <div className="bh-auth__layout">
        <aside className="bh-auth__aside">
          <BhabiWordmark />
          <div className="bh-mini-fan" aria-hidden="true">
            <span>A</span>
            <span data-red="true">♥</span>
            <span>♣</span>
            <span data-red="true">♦</span>
          </div>
          <p className="bh-lead">
            The Punjabi card game. Deal a table, invite your friends — and
            don&apos;t be the Bhabi.
          </p>
        </aside>

        <main className="bh-auth__card" id="main" onFocus={keepFieldVisible}>
          {(eyebrow || title || subtitle) && (
            <header
              className="bh-stack"
              style={{ ['--bh-gap' as string]: '4px', marginBottom: 14 }}
            >
              {eyebrow && <p className="bh-eyebrow">{eyebrow}</p>}
              {title && <h1 className="bh-h2">{title}</h1>}
              {subtitle && (
                <p className="bh-muted bh-small" style={{ margin: 0 }}>
                  {subtitle}
                </p>
              )}
            </header>
          )}
          {children}
          {footer}
        </main>
      </div>
    </div>
  );
};

export type MainFrameProps = {
  stackProps?: StackProps;
  linkPath?: string;
  hideLink?: boolean;
  forgotPasswordPath?: string;
  texts?: Texts<'linkMessage' | 'forgotPassword'>;
  otherSignInOptions?: React.ReactNode;
} & AuthFrameProps;

export const MainFrame: FC<PropsWithChildren<MainFrameProps>> = ({
  children,
  stackProps,
  linkPath,
  hideLink = false,
  forgotPasswordPath,
  otherSignInOptions,
  texts = {
    linkMessage: 'Do not have an account? Sign up here',
    forgotPassword: 'Forgot password?',
  },
  ...frame
}) => (
  <AuthFrame
    {...frame}
    footer={
      <>
        {otherSignInOptions && (
          <div className="bh-auth__alt">{otherSignInOptions}</div>
        )}
        {(!hideLink || forgotPasswordPath) && (
          <nav className="bh-auth__links" aria-label="Account options">
            {forgotPasswordPath && texts.forgotPassword && (
              <Link to={forgotPasswordPath}>{texts.forgotPassword}</Link>
            )}
            {hideLink ? null : (
              <Link to={linkPath || '/'}>{texts.linkMessage}</Link>
            )}
          </nav>
        )}
      </>
    }
  >
    <Stack
      component="form"
      noValidate
      gap={1.5}
      alignItems="stretch"
      {...stackProps}
    >
      {children}
    </Stack>
  </AuthFrame>
);
